import React, { useEffect, useState } from "react";
import { BASE_URL, getMemberProfile, updateMemberProfile } from "../api/axios";
import { useAuth } from "../auth/AuthContext";
import { formatPhone } from "../utils/phone";

function splitPipes(value) {
  return String(value || "").split("|").map((s) => s.trim());
}

const ALL_SPECIALTIES = [
  "Allergy & Immunology","Anesthesiology","Cardiology","Cardiothoracic Surgery",
  "Colorectal Surgery","Critical Care Medicine","Dentistry & Oral Surgery","Dermatology",
  "Emergency Medicine","Endocrinology","Family Medicine","Gastroenterology",
  "General Practice","General Surgery","Geriatrics","Hematology","Hepatology",
  "Infectious Disease","Internal Medicine","Nephrology","Neurology","Neurosurgery",
  "Obstetrics & Gynecology","Oncology","Ophthalmology","Orthopedic Surgery",
  "Otolaryngology (ENT)","Pain Management","Pathology","Pediatrics",
  "Physical Medicine & Rehabilitation","Plastic Surgery","Psychiatry","Pulmonology",
  "Radiology","Rheumatology","Sleep Medicine","Sports Medicine","Urology","Vascular Surgery",
];

const emptyForm = {
  name: "", title: "", academic_title: "", academic_affiliation: "",
  medical_school_affiliation: "", specialty: "", subspecialty: "",
  graduation_degree: "", graduation_year: "", board_certification: "",
  website_address: "", experience: "",
  gender: "", languages: "", email: "", phone: "", practice: "",
  street: "", city: "", state: "", zip: "", county: "",
  hospital_1: "", hospital_2: "", hospital_3: "", hospital_4: "",
  awards: "", bio: "", accepting_patients: "0",
  password: "", confirm_password: "",
};

function buildAddress({ street, city, state, zip, county }) {
  return [street, city, state, zip, county].filter(Boolean).join(" | ");
}

export default function AddDoctorModal({ open, onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1 = form, 2 = done
  const [error, setError] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // When a logged-in user opens this, default to listing themselves.
  // Checking "list another doctor" switches to creating a new account.
  const [listingOther, setListingOther] = useState(false);

  // Only real members can list themselves (prefill their own profile).
  // Admins and logged-out visitors always get a blank "create a listing" form.
  const canSelfList = !!user && user.role === "member";
  const isSelf = canSelfList && !listingOther;

  // Prefill the form from the logged-in user's own profile when listing self.
  useEffect(() => {
    if (!open || !canSelfList || listingOther) return;
    let active = true;
    (async () => {
      try {
        const res = await getMemberProfile();
        const p = res?.data?.profile || {};
        if (!active) return;
        const addr = splitPipes(p.address);
        const hosp = splitPipes(p.hospital_affiliations).filter(Boolean);
        setForm((prev) => ({
          ...prev,
          name: p.name || user.name || "",
          email: p.email || user.email || "",
          phone: p.phone || "",
          title: p.title || "",
          academic_title: p.academic_title || "",
          academic_affiliation: p.academic_affiliation || "",
          medical_school_affiliation: p.medical_school_affiliation || "",
          specialty: p.specialty || "",
          subspecialty: p.subspecialty || "",
          graduation_degree: p.graduation_degree || "",
          graduation_year: p.graduation_year || "",
          board_certification: p.board_certification || "",
          website_address: p.website_address || "",
          experience: p.experience || "",
          gender: p.gender || "",
          languages: p.languages || "",
          practice: p.practice || "",
          street: addr[0] || "",
          city: addr[1] || p.location || "",
          state: addr[2] || "",
          zip: addr[3] || "",
          county: addr[4] || "",
          hospital_1: hosp[0] || "",
          hospital_2: hosp[1] || "",
          hospital_3: hosp[2] || "",
          hospital_4: hosp[3] || "",
          awards: p.awards || "",
          bio: p.bio || "",
          accepting_patients: String(p.accepting_patients) === "1" ? "1" : "0",
        }));
      } catch (_) {
        // fall back to just name/email from the session
        if (active) setForm((prev) => ({ ...prev, name: user.name || "", email: user.email || "" }));
      }
    })();
    return () => { active = false; };
  }, [open, user, listingOther]);

  if (!open) return null;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Toggle between "listing myself" and "listing another doctor".
  const handleToggleOther = (checked) => {
    setListingOther(checked);
    setError("");
    if (checked) {
      // Creating for someone else — clear identity + require a fresh password.
      setForm((prev) => ({ ...prev, name: "", email: "", phone: "", password: "", confirm_password: "" }));
      setProfileImage(null);
      setProfilePreview(null);
    }
    // Unchecking re-triggers the prefill effect above.
  };

  const handleProfileImage = (e) => {
    const file = e.target.files?.[0] || null;
    setProfileImage(file);
    try {
      setProfilePreview(file ? URL.createObjectURL(file) : null);
    } catch (_) {
      setProfilePreview(null);
    }
  };

  const handleClose = () => {
    setForm(emptyForm);
    setProfileImage(null);
    setProfilePreview(null);
    setSubmitting(false);
    setError("");
    setStep(1);
    setSubmittedEmail("");
    setShowPassword(false);
    setListingOther(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Password only required when creating a new account (not when updating your own profile).
    if (!isSelf) {
      if (form.password.length < 6) {
        setError("Password must be at least 6 characters.");
        return;
      }
      if (form.password !== form.confirm_password) {
        setError("Passwords do not match.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const hospitals = [form.hospital_1, form.hospital_2, form.hospital_3, form.hospital_4]
        .map((h) => h.trim()).filter(Boolean).join(" | ");

      const education = [
        form.graduation_degree && form.graduation_year
          ? `${form.graduation_degree} (${form.graduation_year})`
          : form.graduation_degree || form.graduation_year,
        form.academic_title, form.academic_affiliation, form.medical_school_affiliation,
      ].filter(Boolean).join("; ");

      const fullAddress = buildAddress({ street: form.street, city: form.city, state: form.state, zip: form.zip, county: form.county });
      const location = form.city.trim();

      const fields = {
        name: form.name,
        title: form.title,
        specialty: form.specialty,
        subspecialty: form.subspecialty,
        email: form.email,
        phone: form.phone,
        location,
        practice: form.practice,
        website_address: form.website_address,
        board_certification: form.board_certification,
        address: fullAddress,
        bio: form.bio,
        education,
        experience: form.experience,
        gender: form.gender,
        languages: form.languages,
        accepting_patients: form.accepting_patients,
        hospital_affiliations: hospitals,
        awards: form.awards,
        academic_title: form.academic_title,
        academic_affiliation: form.academic_affiliation,
        medical_school_affiliation: form.medical_school_affiliation,
        graduation_degree: form.graduation_degree,
        graduation_year: form.graduation_year,
      };

      if (isSelf) {
        // Update the logged-in user's own physician profile — no new account.
        let payload;
        if (profileImage) {
          payload = new FormData();
          Object.entries(fields).forEach(([k, v]) => payload.append(k, v ?? ""));
          payload.append("profile_image", profileImage);
        } else {
          payload = fields;
        }
        const res = await updateMemberProfile(payload);
        if (!res?.success) throw new Error(res?.message || "Failed to update your profile.");
        setSubmittedEmail(form.email);
        setStep(2);
        return;
      }

      // New listing (yourself first time, or another doctor) — creates an account.
      const fd = new FormData();
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v ?? ""));
      fd.append("password", form.password);
      if (profileImage) fd.append("profile_image", profileImage);

      const res = await fetch(`${BASE_URL}/doctors/public-apply.php`, { method: "POST", body: fd });
      let data = null;
      try { data = await res.json(); } catch (_) {}
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || `Server error (${res.status}). Please try again.`);
      }

      setSubmittedEmail(form.email);
      setStep(2);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const addressPreview = buildAddress({ street: form.street, city: form.city, state: form.state, zip: form.zip, county: form.county });

  // ── Step 2: All done ────────────────────────────────────────────────────
  if (step === 2) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && handleClose()}>
        <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
            <div className="bg-emerald-100 rounded-full p-5 mb-6">
              <svg className="w-12 h-12 text-[#00342b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h4 className="text-2xl font-bold text-gray-800 mb-3">You're All Set!</h4>
            <p className="text-gray-500 max-w-sm mb-2">
              {isSelf
                ? "Your profile has been updated and will be reviewed before any changes go live."
                : "Your listing has been submitted for review and the account is ready."}
            </p>
            <p className="text-gray-400 text-sm mb-8">
              {isSelf ? "Signed in as: " : "Log in any time using this email: "}
              <span className="font-semibold text-gray-600">{submittedEmail}</span>
            </p>
            <button onClick={handleClose} className="bg-[#00342b] text-white px-8 py-3 rounded-xl font-semibold hover:bg-[#00493c] transition">
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 1: Main doctor form ────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl my-8 overflow-hidden">
        {/* Header */}
        <div className="bg-[#00342b] px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white/10 rounded-xl p-2">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Join Our Physician Directory</h3>
              <p className="text-white/70 text-sm">
                {isSelf
                  ? "Complete your physician profile — details will be reviewed before going live"
                  : "Create an account — your profile will be reviewed before going live"}
              </p>
            </div>
          </div>
          <button type="button" onClick={handleClose} className="text-white/70 hover:text-white transition text-3xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-8" autoComplete="off">
          {/* Decoy fields absorb the browser's saved-login autofill so the real
              Email/Password fields below stay empty (e.g. admin credentials). */}
          <div aria-hidden="true" style={{ position: "absolute", height: 0, width: 0, overflow: "hidden", opacity: 0 }}>
            <input type="text" name="fakeusernameremembered" tabIndex={-1} autoComplete="username" />
            <input type="password" name="fakepasswordremembered" tabIndex={-1} autoComplete="new-password" />
          </div>

          {/* Members only: toggle list yourself vs. another physician */}
          {canSelfList && (
            <label className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={listingOther}
                onChange={(e) => handleToggleOther(e.target.checked)}
                className="w-5 h-5 accent-[#00342b] mt-0.5"
              />
              <span className="text-sm">
                <span className="font-semibold text-gray-800">I'm listing another physician (not myself)</span>
                <span className="block text-gray-500 text-xs mt-0.5">
                  {listingOther
                    ? "Enter the other doctor's details and set a password to create their account."
                    : "Your name and email are filled in from your account. Leave this unchecked to update your own profile."}
                </span>
              </span>
            </label>
          )}

          {/* ── 1. Personal Information ── */}
          <Section label="Personal Information">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Full Name" required value={form.name} onChange={(v) => setField("name", v)} placeholder="Dr. Fatima Al-Hassan" />
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Gender <Required /></label>
                <select value={form.gender} onChange={(e) => setField("gender", e.target.value)} required className={selectCls}>
                  <option value="">Select…</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <Field label="Phone Number" required value={form.phone} onChange={(v) => setField("phone", formatPhone(v))} placeholder="1-917-903-8922" />
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address <Required /></label>
                <input
                  type="email"
                  name="reg_email"
                  required
                  autoComplete="off"
                  readOnly
                  onFocus={(e) => e.target.removeAttribute("readonly")}
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder="dr.name@example.com"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white text-sm focus:ring-2 focus:ring-[#00342b] focus:border-[#00342b] outline-none"
                />
              </div>
              <Field label="Graduation Degree" required value={form.graduation_degree} onChange={(v) => setField("graduation_degree", v)} placeholder="MD, MBBS, DO, FCPS" />
              <Field label={<>Year Graduated <span className="text-gray-400 font-normal">(optional)</span></>} value={form.graduation_year} onChange={(v) => setField("graduation_year", v)} placeholder="2005" type="number" min="1950" max={new Date().getFullYear()} />
              <Field label="Years of Experience" required value={form.experience} onChange={(v) => setField("experience", v)} placeholder="12" type="number" min="0" />
              <Field label="Languages Spoken" required value={form.languages} onChange={(v) => setField("languages", v)} placeholder="English, Arabic, Urdu" />
            </div>
          </Section>

          {/* ── 2. Practice & Contact ── */}
          <Section label="Practice & Contact">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Practice Name" required value={form.practice} onChange={(v) => setField("practice", v)} placeholder="Islamic Medical Associates" />
              <Field label="Website Address" required value={form.website_address} onChange={(v) => setField("website_address", v)} placeholder="https://yourpractice.com" type="url" />
            </div>
            <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 p-4 space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-[#00342b]/60 mb-1">Full Practice Address</p>
              <p className="text-xs text-gray-400 -mt-1">These fields are combined and saved as your full address.</p>
              <Field label="Street Address" value={form.street} onChange={(v) => setField("street", v)} placeholder="123 Main Street, Suite 400" />
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="City" value={form.city} onChange={(v) => setField("city", v)} placeholder="Houston" />
                <Field label="State / Province" value={form.state} onChange={(v) => setField("state", v)} placeholder="TX" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="ZIP / Postal Code" value={form.zip} onChange={(v) => setField("zip", v)} placeholder="77001" />
                <Field label={<>County <span className="text-gray-400 font-normal">(optional)</span></>} value={form.county} onChange={(v) => setField("county", v)} placeholder="Harris County" />
              </div>
              {addressPreview && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2 mt-1">
                  <p className="text-[11px] uppercase tracking-widest text-[#00342b]/50 font-semibold mb-0.5">Preview</p>
                  <p className="text-sm text-[#00342b]">{addressPreview}</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 mt-4">
              <input type="checkbox" id="pub_accepting" checked={form.accepting_patients === "1"} onChange={(e) => setField("accepting_patients", e.target.checked ? "1" : "0")} className="w-5 h-5 accent-[#00342b]" />
              <label htmlFor="pub_accepting" className="text-sm font-semibold text-gray-700 cursor-pointer">I am currently accepting new patients</label>
            </div>
          </Section>

          {/* ── 3. Credentials & Title ── */}
          <Section label="Credentials & Title">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Medical Title / Credentials" required value={form.title} onChange={(v) => setField("title", v)} placeholder="MD, FACC, FACS" hint="Post-nominal letters after your name" />
              <Field label="Board Certification" required value={form.board_certification} onChange={(v) => setField("board_certification", v)} placeholder="American Board of Internal Medicine" hint="Your certifying board(s)" />
              <Field label="Academic Title" value={form.academic_title} onChange={(v) => setField("academic_title", v)} placeholder="Associate Professor, Clinical Instructor" hint="Your academic rank or position, if applicable" />
              <Field label="Academic Affiliation" value={form.academic_affiliation} onChange={(v) => setField("academic_affiliation", v)} placeholder="Department of Medicine, XYZ University" hint="Your academic department, institute, or faculty" />
              <Field label="Medical School Attended" value={form.medical_school_affiliation} onChange={(v) => setField("medical_school_affiliation", v)} placeholder="Harvard Medical School" className="md:col-span-2" hint="Medical school the doctor attended" />
            </div>
          </Section>

          {/* ── 4. Specialty ── */}
          <Section label="Specialty">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Specialty <Required /></label>
                <select value={form.specialty} onChange={(e) => setField("specialty", e.target.value)} required className={selectCls}>
                  <option value="">Select specialty…</option>
                  {ALL_SPECIALTIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <Field label="Subspecialty" value={form.subspecialty} onChange={(v) => setField("subspecialty", v)} placeholder="e.g. Interventional Cardiology, Pediatric Neurology" hint="Your specific area of focus within your specialty" />
            </div>
          </Section>

          {/* ── 5. Hospital Affiliations (optional) ── */}
          <Section label={<>Hospital Affiliations <span className="text-gray-400 font-normal normal-case tracking-normal">(optional)</span></>}>
            <p className="text-xs text-gray-500 mb-3">List up to four hospitals or medical centers where you practice.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Hospital 1" value={form.hospital_1} onChange={(v) => setField("hospital_1", v)} placeholder="Massachusetts General Hospital" />
              <Field label="Hospital 2" value={form.hospital_2} onChange={(v) => setField("hospital_2", v)} placeholder="Optional" />
              <Field label="Hospital 3" value={form.hospital_3} onChange={(v) => setField("hospital_3", v)} placeholder="Optional" />
              <Field label="Hospital 4" value={form.hospital_4} onChange={(v) => setField("hospital_4", v)} placeholder="Optional" />
            </div>
          </Section>

          {/* ── 6. Awards & Recognitions ── */}
          <Section label="Special Awards & Recognitions">
            <textarea value={form.awards} onChange={(e) => setField("awards", e.target.value)} rows={3} className={textareaCls} placeholder="e.g. Best Physician Award 2022 — American Medical Association…" />
          </Section>

          {/* ── 7. About / Bio (optional) ── */}
          <Section label={<>About You <span className="text-gray-400 font-normal normal-case tracking-normal">(optional)</span></>}>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Additional Information</label>
            <textarea value={form.bio} onChange={(e) => setField("bio", e.target.value)} rows={5} className={textareaCls} placeholder="Please include any additional information that will help your patients learn about you." />
          </Section>

          {/* ── 8. Profile Photo ── */}
          <Section label="Profile Photo">
            <div className="flex items-start gap-5">
              {profilePreview ? (
                <div className="relative w-24 h-24 rounded-2xl overflow-hidden border-2 border-[#00342b]/20 shrink-0">
                  <img src={profilePreview} alt="Preview" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => { setProfileImage(null); setProfilePreview(null); }}
                    className="absolute top-1 right-1 bg-white rounded-full w-5 h-5 text-xs text-red-600 font-bold shadow flex items-center justify-center">×</button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center shrink-0">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
              <div>
                <input type="file" accept="image/*" onChange={handleProfileImage}
                  className="text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#00342b]/10 file:text-[#00342b] file:font-semibold file:cursor-pointer" />
                <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP — max 5 MB</p>
              </div>
            </div>
          </Section>

          {/* ── 9. Account Security ── (hidden when updating your own profile) */}
          {!isSelf && (
          <Section label="Account Security">
            <p className="text-xs text-gray-500 mb-3">
              {listingOther
                ? "Set a password for this doctor's account so they can log in later."
                : "Set a password so you can log in to manage your profile, forum posts, and job listings."}
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Password <Required /></label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} value={form.password} onChange={(e) => setField("password", e.target.value)} required minLength={6}
                    name="reg_secret"
                    autoComplete="new-password"
                    readOnly
                    onFocus={(e) => e.target.removeAttribute("readonly")}
                    placeholder="Minimum 6 characters"
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-11 bg-white text-sm focus:ring-2 focus:ring-[#00342b] focus:border-[#00342b] outline-none" />
                  <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
              <Field label="Confirm Password" required value={form.confirm_password} onChange={(v) => setField("confirm_password", v)} placeholder="Re-enter your password" type={showPassword ? "text" : "password"} />
            </div>
          </Section>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex gap-2 items-start">
              <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex gap-3">
            <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-amber-800">
              Your listing will be reviewed by our team before it goes live. You'll be contacted at the email provided.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={handleClose} disabled={submitting}
              className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-600 font-medium disabled:opacity-50 hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="px-6 py-2.5 rounded-xl bg-[#00342b] text-white font-semibold disabled:opacity-60 flex items-center gap-2 hover:bg-[#00493c] transition shadow-lg">
              {submitting && (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              )}
              {submitting ? "Submitting…" : isSelf ? "Save My Profile" : "Submit & Create Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const selectCls = "w-full border border-gray-300 rounded-xl px-4 py-3 bg-white text-sm focus:ring-2 focus:ring-[#00342b] focus:border-[#00342b] outline-none";
const textareaCls = "w-full border border-gray-300 rounded-xl px-4 py-3 bg-white text-sm focus:ring-2 focus:ring-[#00342b] focus:border-[#00342b] outline-none";

function Required() {
  return <span className="text-red-500 ml-0.5">*</span>;
}

function Section({ label, children }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-widest text-[#00342b]/60 mb-3 pb-1.5 border-b border-gray-100">{label}</p>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, className = "", type = "text", required = false, hint, min, max }) {
  return (
    <div className={className}>
      <label className="block text-sm font-semibold text-gray-700 mb-1">
        {label} {required && <Required />}
      </label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        required={required} min={min} max={max}
        className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white text-sm focus:ring-2 focus:ring-[#00342b] focus:border-[#00342b] outline-none" />
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}
